/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import LoaderWrapper from "../components/loaders/loader";
import Navbar from "../components/navigation/navbar/Navbar";
import Sidebar from "../components/navigation/sidebar/Sidebar";
import { ANALYTICS_PERMISSIONS, API, API_BASEURL, PERMISSIONS, ROUTES, SWR_CONFIG, buildUrl } from "../config";
import {
    auth0Id,
    lastOpportunityRouteState,
    loadingAuth,
    programsToValidate,
    unreadNotifications,
    userInfo,
    userPermissions,
} from "../config/recoil/atoms";
import { usePermissions } from "../hooks";
import useGetSWR from "../hooks/useGetSWR";
import {
    ActivityLogo,
    CalendarLogo,
    CampaignsLogo,
    ClientsLogo,
    DashboardLogo,
    EmployeesIcon,
    LeadsLogo,
    OpportunityIcon,
    SettingsIcon,
    UsersLogo,
    TutorialIcon,
    AnalyticsIcon,
    BusinessIcon,
    ChatIcon,
    PaymentsIcon,
    TeamHubIcon,
} from "../icons";
import { ApplicationRoutes } from "../routes";
import { IsingleUserRes, NotificationListRes } from "../types";
import { LegalTextAcceptanceModal } from "../components/modals/LegalTextAcceptanceModal";
import { resolveTeamHubLanding } from "../containers/teamhub/teamhub-landing";

export interface MenuItem {
    name: string;
    tooltip: string;
    url: string;
    active: boolean;
    icon: any;
    notification?: number;
}

function MainScreen() {
    /**
     * Permissions functions
     */
    const { checkPermission } = usePermissions();
    const info = useRecoilValue(userInfo);
    const authId = useRecoilValue(auth0Id);
    const [unreadNotificationsValue, setUnreadNotifications] = useRecoilState(unreadNotifications);
    const setUserInfo = useSetRecoilState(userInfo);
    const setLoadingAuth = useSetRecoilState(loadingAuth);
    const [userPermissionsValue, setUserPermissions] = useRecoilState(userPermissions);
    const loading = useRecoilValue(loadingAuth);

    const { logout } = useAuth0();
    const url = buildUrl(API.PROFILE, { id: authId || "0" }, API_BASEURL); // build url

    const { data: userRes, error } = useGetSWR<IsingleUserRes>(
        authId ? url : null,
        SWR_CONFIG.FORMS
    ); // get form data

    // Unread Notifications
    const urlUnreadNotifications = buildUrl(API.NOTIFICATIONS_UNREAD, {}, API_BASEURL);

    const { data: notifications } = useGetSWR<NotificationListRes>(urlUnreadNotifications, {
        ...SWR_CONFIG.UNREAD_COUNT,
    });

    useEffect(() => {
        if (notifications) {
            setUnreadNotifications(notifications);
            setMenu((prev) => {
                const newMenu = [...prev];
                const conversation = newMenu.findIndex((m) => m.name === "Conversations");
                if (conversation !== -1) {
                    newMenu[conversation].notification = notifications.unread_messages || 0;
                }
                return [...newMenu];
            });
        }
    }, [notifications]);

    // Get real time notifications for menu icon
    const realtimeNotifications = useRecoilValue(programsToValidate);

    const lastOpportunityType = useRecoilValue(lastOpportunityRouteState);

    useEffect(() => {
        setMenu((prev) => {
            const n = realtimeNotifications.filter(
                (r) => r.status === "validated" || r.status === "waiting"
            );
            const newMenu = [...prev];
            newMenu[0] = {
                ...newMenu[0],
                notification: n.length,
            };
            return [...newMenu];
        });
    }, [realtimeNotifications]);
    /**
     * Generic User Menu Links
     */
    const [menu, setMenu] = useState<MenuItem[]>([
        {
            name: "Dashboard",
            tooltip: "Dashboard",
            url: "/dashboard",
            active: true,
            icon: DashboardLogo,
            notification: 0,
        },
    ]);

    useEffect(() => {
        if (error && (error as any).status === 401) {
            // logout({ returnTo: process.env.REACT_APP_URL });
            console.log(error);
        }
    }, [error]);

    useEffect(() => {
        if (userRes?.permissions) {
            // Set user permissions
            setUserPermissions(userRes.permissions);
        }
        if (userRes !== undefined) {
            if (userRes.user && userRes.code === 200 && userRes.success) {
                setLoadingAuth(false);
                /**
                 * Set user info
                 */
                setUserInfo({
                    name: userRes?.user?.name || "",
                    profile_image: userRes?.user?.profile_image || info.profile_image,
                    person_id: userRes?.user?.person_id || "",
                });
                /**
                 * Set user on datadog session
                 */
                datadogRum.setUser({
                    id: userRes?.user?.person_id || "",
                    email: userRes?.user?.email || "",
                    name: userRes?.user?.name || "",
                });
            } else {
                // If any error in response, logout
                logout();
            }
        }
    }, [userRes]);

    useEffect(() => {
        /**
         * Set Menu according to user's permissions
         */
        if (userPermissionsValue.length > 0) {
            setMenu(getMenu());
        }
    }, [userPermissionsValue, lastOpportunityType]);

    const getOpportunityRoute = (type: "Buyer & Seller" | "CR" | "Mortgage" | "HELOC") => {
        switch (type) {
            case "CR":
                return ROUTES.CR_OPPORTUNITIES;
            case "Mortgage":
                return ROUTES.MORTGAGE_OPPORTUNITIES;
            case "HELOC":
                return ROUTES.HELOC_OPPORTUNITIES;
            case "Buyer & Seller":
            default:
                return ROUTES.BUYER_SELLER_OPPORTUNITIES;
        }
    };

    const getMenu = () => {
        const menu = [];
        menu.push({
            name: "Dashboard",
            tooltip: "Dashboard",
            url: "/dashboard",
            active: true,
            icon: DashboardLogo,
            notification: 0,
        });
        if (checkPermission(PERMISSIONS.LEAD_ACCESS)) {
            menu.push({
                name: "Leads",
                tooltip: "Leads",
                url: ROUTES.LEADS,
                active: true,
                icon: LeadsLogo,
            });
        }
        if (checkPermission(PERMISSIONS.CLIENT_ACCESS)) {
            menu.push({
                name: "Clients",
                tooltip: "Clients",
                url: ROUTES.CLIENTS,
                active: true,
                icon: ClientsLogo,
            });
        }
        if (checkPermission(PERMISSIONS.BUSINESS_ACCESS_TAB)) {
            menu.push({
                name: "Business",
                tooltip: "Business",
                url: ROUTES.BUSINESS,
                active: true,
                icon: BusinessIcon,
            });
        }
        if (
            checkPermission(PERMISSIONS.READ_CONVERSATION) ||
            checkPermission(PERMISSIONS.READ_CONVERSATION_OWN)
        ) {
            menu.push({
                name: "Conversations",
                tooltip: "Conversations",
                url: "/conversations",
                active: true,
                icon: ChatIcon,
            });
        }
        if (checkPermission(PERMISSIONS.OPPORTUNITY_ACCESS_TAB)) {
            menu.push({
                name: "Opportunities",
                tooltip: "Opportunities",
                url: getOpportunityRoute(lastOpportunityType),
                active: true,
                icon: OpportunityIcon,
            });
        }
        if (checkPermission(PERMISSIONS.EMPLOYEE_ACCESS)) {
            menu.push({
                name: "Employees",
                tooltip: "Employees",
                url: ROUTES.EMPLOYEES,
                active: true,
                icon: EmployeesIcon,
            });
        }
        if (checkPermission(PERMISSIONS.ACTIVITY_ACCESS)) {
            menu.push({
                name: "Activity",
                tooltip: "Activity",
                url: "/activities",
                active: true,
                icon: ActivityLogo,
            });
        }
        if (checkPermission(PERMISSIONS.CAMPAIGN_ACCESS)) {
            menu.push({
                name: "Communications",
                tooltip: "Communications",
                url: "/comunications",
                active: true,
                icon: CampaignsLogo,
            });
        }
        if (checkPermission(PERMISSIONS.USER_ACCESS)) {
            menu.push({
                name: "Users",
                tooltip: "Users",
                url: "/users",
                active: true,
                icon: UsersLogo,
            });
        }
        if (checkPermission(PERMISSIONS.CALENDAR_ACCESS)) {
            menu.push({
                name: "Calendar",
                tooltip: "Calendar",
                url: "/calendar",
                active: true,
                icon: CalendarLogo,
            });
        }
        if (
            checkPermission(PERMISSIONS.ANALYTICS_ACCESS) &&
            ANALYTICS_PERMISSIONS.some((p) => userPermissionsValue.includes(p))
        ) {
            menu.push({
                name: "Analytics",
                tooltip: "Analytics",
                url: "/analytics",
                active: true,
                icon: AnalyticsIcon,
            });
        }
        if (checkPermission(PERMISSIONS.TUTORIAL_ACCESS)) {
            menu.push({
                name: "Tutorials",
                tooltip: "Tutorials",
                url: "/tutorials",
                active: true,
                icon: TutorialIcon,
            });
        }
        if (checkPermission(PERMISSIONS.PAYMENTS_ACCESS)) {
            menu.push({
                name: "Payments",
                tooltip: "Payments",
                url: "/payments",
                active: true,
                icon: PaymentsIcon,
            });
        }
        if (checkPermission(PERMISSIONS.TEAMHUB_ACCESS)) {
            // The tab permission alone does not guarantee a reachable sub-section,
            // so land on the first one the user can actually access.
            const teamHubLanding = resolveTeamHubLanding(checkPermission);
            if (teamHubLanding) {
                menu.push({
                    name: "TeamHub",
                    tooltip: "TeamHub",
                    url: teamHubLanding,
                    active: true,
                    icon: TeamHubIcon,
                });
            }
        }
        if (checkPermission(PERMISSIONS.SETTINGS_ACCESS)) {
            menu.push({
                name: "Settings",
                tooltip: "Settings",
                url: "/settings",
                active: true,
                icon: SettingsIcon,
            });
        }
        return menu;
    };

    /**
     * Show a loader while we wait for api response
     */
    if (loading) {
        return <LoaderWrapper />;
    }

    return (
        <div className="flex flex-col md:flex-row relative">
            <LegalTextAcceptanceModal />
            <Sidebar menu={menu} />
            <div
                className="flex flex-col flex-1
      min-h-screen bg-color5 pt-8 min-w-0
      md:pt-0 pb-12 relative w-full"
            >
                <Navbar hide={false} />
                {/* <Unauthorized /> */}
                <div className="">
                    <div className="reyna_container max-w-none">
                        {/* All the application routes are in this component */}
                        <ApplicationRoutes />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MainScreen;
